<template>
    <div>
        <announcement-banner :banner="announcementBanner" />
        <div class="flex lg:flex-row flex-col">
            <div class="lg:w-1/4 w-full px-4 lg:mt-24 mt-4 lg:mr-6 mr-0">
                <div class="bg-primary-500 rounded-2xl p-5">
                    <h3 class="text-white">Filter Programs</h3>
                    <div class="pb-4">
                        <filter-accordion v-if="categories.length > 0" title="Category" class="mt-4">
                            <d-checkbox v-for="category in categories"
                                        :key="category.id"
                                        v-model="categoryFilters"
                                        class="ml-2 py-1 pt-1 font-bold"
                                        white-checkmark
                                        :native-value="category.name">
                                <div class="flex items-center">
                                    <span>{{ category.name }}</span>
                                </div>
                            </d-checkbox>
                        </filter-accordion>

                        <d-btn block
                               color="secondary-500"
                               class="mt-4"
                               @click="clearAllFilters">
                            Clear All Filters
                        </d-btn>
                    </div>
                </div>
            </div>
            <div class="lg:w-3/4 w-full">
                <div v-if="orgSettings.homePageHtml" v-html="orgSettings.homePageHtml"></div>

                <h1 v-if="!orgSettings.homePageHtml" class="font-bold">
                    Select a Program
                </h1>
                <section v-if="!orgSettings.accountsDisabled">
                    <h2 class="font-bold text-2xl lg:pt-8">
                        My Programs
                    </h2>
                    <div class="w-full lg:mt-4 lg:mb-10 mb-5 lg:pb-0 pb-5">
                        <div v-if="loggedIn && !hasRole('Anonymous')">
                            <div class="flex flex-wrap -mx-3">
                                <div v-for="subjectArea in filteredUserSubjectAreas"
                                     :key="subjectArea.id"
                                     class="lg:w-1/3 md:w-1/2 w-full lg:px-3 p-3"
                                     @click="goToUrl(`/area/${subjectArea.id}`)">
                                    <div class="rounded-3xl relative overflow-hidden lg:h-40 h-48 cursor-pointer">
                                        <div class="bg-cover bg-no-repeat absolute inset-0 bg-image-blur"
                                             :style="{'background-image': `url(${subjectArea.imageUrl})`}" />
                                        <div class="inset-0 absolute border-8 border-primary-500 border-transparent"
                                             style="background-color:rgb(56, 163, 165 ,0.56);" />
                                        <div class="absolute text-white text-2xl font-bold text-center flex justify-center items-center h-full w-full top-0 left-0 text-shadow p-3">
                                            {{ subjectArea.name }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <p class="text-xl">
                                Log in to see personalized content within your programs. Not sure how to get started? Contact {{ orgSettings.name }} by phone at <a :href="'tel:+1'+orgSettings.supportPhone" class="underline">{{ orgSettings.supportPhone }}</a>.
                            </p>
                            <div class="pt-4">
                                <d-btn color="secondary-500" href="/login">
                                    Login
                                </d-btn>
                            </div>
                        </div>
                    </div>
                </section>
                <section v-if="filteredAllSubjectAreas.length > 0">
                    <h2 class="font-bold text-2xl">
                        All Programs
                    </h2>
                    <div class="w-full lg:mt-4 lg:mb-10 lg:pb-0 pb-5">
                        <div class="flex flex-wrap -mx-3">
                            <div v-for="subjectArea in filteredAllSubjectAreas"
                                 :key="subjectArea.id"
                                 class="lg:w-1/3 md:w-1/2 w-full lg:px-3 p-3"
                                 @click="goToUrl(`/area/${subjectArea.id}`)">
                                <div class="rounded-3xl relative overflow-hidden lg:h-40 h-48 cursor-pointer">
                                    <div class="bg-cover bg-no-repeat absolute inset-0 bg-image-blur"
                                         :style="{'background-image': `url(${subjectArea.imageUrl})`}" />
                                    <div class="inset-0 absolute border-8 border-primary-500 border-transparent"
                                         style="background-color:rgb(56, 163, 165 ,0.56);" />
                                    <div class="absolute text-white text-2xl font-bold text-center flex justify-center items-center h-full w-full top-0 left-0 text-shadow p-3">
                                        {{ subjectArea.name }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
    import axios from 'axios';
    import { useAuthStore } from '@stores/auth';
    import FilterAccordion from '../SubjectAreas/components/FilterAccordion.vue';

    export default {
        parent: true,
        props: {
            orgSettings: {
                type: Object,
                default: () => null,
            },
            announcementBanner: {
                type: Object,
                default: () => null,
            },
        },
        components: {
            FilterAccordion,
        },
        data() {
            return {
                userSubjectAreas: [],
                allSubjectAreas: [],
                categories: [],
                categoryFilters: JSON.parse(localStorage.getItem('home-categoryFilters')) || [],
                auth: null,
            };
        },
        computed: {
            // Use Pinia auth store's properties
            profile() {
                return this.auth ? this.auth.profile || {} : {};
            },
            loggedIn() {
                return this.auth ? this.auth.loggedIn : false;
            },
            hasRole() {
                if (!this.auth) return () => false;
                return this.auth.hasRole;
            },
            filteredUserSubjectAreas() {
                return this.filterAndSort(this.userSubjectAreas);
            },
            filteredAllSubjectAreas() {
                return this.filterAndSort(this.allSubjectAreas);
            },
        },
        mounted() {
            this.auth = useAuthStore();
            this.initializeData();
        },
        watch: {
            categoryFilters: {
                handler(newValue) {
                    localStorage.setItem('home-categoryFilters', JSON.stringify(newValue));
                },
                deep: true,
            },
        },
        methods: {
            async initializeData() {
                if (this.loggedIn) {
                    await this.fetchUserSubjectAreas();
                }
                await this.fetchAllSubjectAreas();
                await this.setCategories();
            },

            async setCategories() {
                const allSubjectAreas = [...this.userSubjectAreas, ...this.allSubjectAreas];
                const categoryMap = new Map();

                allSubjectAreas.forEach(subjectArea => {
                    subjectArea.categories.forEach(category => {
                        const categoryNameKey = category.name.toLowerCase();
                        if (!categoryMap.has(categoryNameKey)) {
                            categoryMap.set(categoryNameKey, category);
                        }
                    });
                });

                this.categories = Array.from(categoryMap.values());

                // Validate stored filters against available categories
                const validCategories = new Set(this.categories.map(c => c.name));
                this.categoryFilters = this.categoryFilters.filter(filter => validCategories.has(filter));
                localStorage.setItem('home-categoryFilters', JSON.stringify(this.categoryFilters));
            },

            async fetchUserSubjectAreas() {
                try {
                    const { data } = await axios.get('/api/subject-areas/user');
                    this.userSubjectAreas = data;
                } catch (error) {
                    console.error('Error fetching user subject areas:', error);
                    this.userSubjectAreas = [];
                }
            },

            async fetchAllSubjectAreas() {
                try {
                    const { data } = await axios.get('/api/subject-areas/all');
                    this.allSubjectAreas = data;
                } catch (error) {
                    console.error('Error fetching all subject areas:', error);
                    this.allSubjectAreas = [];
                }
            },

            filterAndSort(subjectAreas) {
                let filtered = [...subjectAreas];
              
                if (this.categoryFilters.length > 0) {
                    filtered = filtered.filter(subjectArea =>
                        subjectArea.categories.some(category =>
                            this.categoryFilters.includes(category.name)
                        )
                    );
                }

                // Server-side ordering by Order field is now handled by the API
                // No need for client-side sorting
              
                return filtered;
            },

            goToUrl(url) {
                window.location.href = url;
            },

            async clearAllFilters() {
                try {
                    // Clear all filters in data
                    this.categoryFilters = [];

                    // Clear localStorage items
                    localStorage.removeItem('home-categoryFilters');

                    // Clear localforage items for all subject areas
                    const subjectAreas = [...this.userSubjectAreas, ...this.allSubjectAreas];
                    for (const area of subjectAreas) {
                        await localStorage.removeItem(`categoryFilters-${area.id}`);
                        await localStorage.removeItem(`resourceTypeFilters-${area.id}`);
                    }
                } catch (error) {
                    console.error('Error clearing filters:', error);
                }
            },
        },
    };
</script>
